import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/firebase';

/**
 * Firebase Analytics Service
 * 
 * Modular and reusable analytics tracking following best practices:
 * - snake_case event names
 * - ≤25 event parameters per event
 * - Standard primitives: section, feature, action, source, plan_tier
 */

const MAX_PARAMS = 25;

/**
 * Validates event parameters
 * @param {Object} params - Event parameters
 * @param {string} eventName - Event name for error messages
 * @throws {Error} If validation fails
 */
const validateParams = (params, eventName) => {
  if (!params || typeof params !== 'object') {
    throw new Error(`Analytics: params must be an object for event "${eventName}"`);
  }

  const paramCount = Object.keys(params).length;
  if (paramCount > MAX_PARAMS) {
    throw new Error(
      `Analytics: Event "${eventName}" has ${paramCount} parameters, but maximum is ${MAX_PARAMS}`
    );
  }

  // Validate parameter names (should be snake_case)
  Object.keys(params).forEach(key => {
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      console.warn(
        `Analytics: Parameter "${key}" in event "${eventName}" should be snake_case (lowercase with underscores)`
      );
    }
  });
};

/**
 * Core function to log events to Firebase Analytics
 * @param {string} eventName - Event name in snake_case
 * @param {Object} params - Event parameters (max 25)
 */
export const trackEvent = (eventName, params = {}) => {
  if (!analytics) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event would be tracked:', eventName, params);
    }
    return;
  }

  try {
    // Validate event name format
    if (!/^[a-z][a-z0-9_]*$/.test(eventName)) {
      console.warn(
        `Analytics: Event name "${eventName}" should be snake_case (lowercase with underscores)`
      );
    }

    validateParams(params, eventName);

    logEvent(analytics, eventName, params);
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

/**
 * Track screen view
 * @param {Object} options
 * @param {string} options.page_location - Full URL or page path
 * @param {string} options.firebase_screen - Screen name (e.g., "dashboard", "billing")
 * @param {string} [options.section] - Section identifier
 * @param {Object} [options.additionalParams] - Additional parameters (will be merged)
 */
export const trackScreenView = ({ page_location, firebase_screen, section, additionalParams = {} }) => {
  const params = {
    page_location,
    firebase_screen,
    ...(section && { section }),
    ...additionalParams,
  };

  trackEvent('screen_view', params);
};

/**
 * Track feature usage
 * @param {Object} options
 * @param {string} options.section - Section where feature is used (e.g., "editor", "dashboard")
 * @param {string} options.feature - Feature name (e.g., "ai_suggest", "export_data")
 * @param {string} options.action - Action performed (e.g., "invoke", "click", "submit")
 * @param {string} [options.source] - Source of the action (e.g., "button", "menu", "shortcut")
 * @param {string} [options.plan_tier] - User's plan tier (e.g., "free", "pro", "enterprise")
 * @param {Object} [options.additionalParams] - Additional parameters (will be merged)
 */
export const trackFeatureUsed = ({
  section,
  feature,
  action,
  source,
  plan_tier,
  additionalParams = {},
}) => {
  const params = {
    section,
    feature,
    action,
    ...(source && { source }),
    ...(plan_tier && { plan_tier }),
    ...additionalParams,
  };

  trackEvent('feature_used', params);
};

/**
 * Track CTA (Call-to-Action) clicks
 * @param {Object} options
 * @param {string} options.section - Section where CTA is located (e.g., "pricing", "dashboard")
 * @param {string} options.action - Action of the CTA (e.g., "start_trial", "upgrade", "subscribe")
 * @param {string} [options.source] - Source of the click (e.g., "button", "link", "banner")
 * @param {string} [options.plan_tier] - Current plan tier
 * @param {string} [options.target_plan] - Target plan if applicable
 * @param {Object} [options.additionalParams] - Additional parameters (will be merged)
 */
export const trackCTAClick = ({
  section,
  action,
  source,
  plan_tier,
  target_plan,
  additionalParams = {},
}) => {
  const params = {
    section,
    action,
    ...(source && { source }),
    ...(plan_tier && { plan_tier }),
    ...(target_plan && { target_plan }),
    ...additionalParams,
  };

  trackEvent('cta_click', params);
};

/**
 * Track content views
 * @param {Object} options
 * @param {string} options.section - Section where content is located (e.g., "docs", "editor", "dashboard")
 * @param {string} [options.slug] - Content slug/identifier (e.g., "getting-started", "api-reference")
 * @param {string} [options.content_type] - Type of content (e.g., "article", "video", "tutorial")
 * @param {string} [options.content_id] - Unique content identifier
 * @param {string} [options.plan_tier] - User's plan tier
 * @param {Object} [options.additionalParams] - Additional parameters (will be merged)
 */
export const trackContentView = ({
  section,
  slug,
  content_type,
  content_id,
  plan_tier,
  additionalParams = {},
}) => {
  const params = {
    section,
    ...(slug && { slug }),
    ...(content_type && { content_type }),
    ...(content_id && { content_id }),
    ...(plan_tier && { plan_tier }),
    ...additionalParams,
  };

  trackEvent('content_view', params);
};

/**
 * Track custom events with validation
 * Use this for events that don't fit the standard patterns above
 * @param {string} eventName - Event name in snake_case
 * @param {Object} params - Event parameters (max 25)
 */
export const trackCustomEvent = (eventName, params = {}) => {
  trackEvent(eventName, params);
};

/**
 * Helper to get current page location
 * @returns {string} Current page location
 */
export const getCurrentPageLocation = () => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname + window.location.search;
};

/**
 * Helper to get current screen name from route
 * @param {string} pathname - Current pathname
 * @returns {string} Screen name
 */
export const getScreenNameFromPath = (pathname) => {
  if (!pathname || pathname === '/') return 'home';
  
  // Remove leading slash and convert to screen name
  const screen = pathname.replace(/^\//, '').replace(/\//g, '_');
  return screen || 'home';
};

// Export default object for convenience
export default {
  trackEvent,
  trackScreenView,
  trackFeatureUsed,
  trackCTAClick,
  trackContentView,
  trackCustomEvent,
  getCurrentPageLocation,
  getScreenNameFromPath,
};

